import React, { useRef } from 'react';
import { FileIcon, PaperclipIcon, XIcon } from 'lucide-react';
import { MAX_QUOTE_FILES } from '../../data/quote';
import { formatFileSize } from '../../utils/quoteValidation';
interface FileUploadFieldProps {
  files: File[];
  notice: string;
  onAdd: (list: FileList | null) => void;
  onRemove: (index: number) => void;
}
export function FileUploadField({
  files,
  notice,
  onAdd,
  onRemove
}: FileUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const full = files.length >= MAX_QUOTE_FILES;
  const remaining = MAX_QUOTE_FILES - files.length;
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    onAdd(e.target.files);
    e.target.value = '';
  }
  function handleRemove(index: number) {
    onRemove(index);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }
  return <div className="min-w-0" data-id="element-247" data-mp="nydqjlefnb5v">
      <div className="flex items-baseline justify-between gap-3" data-id="element-248" data-mp="iz18rf2f4ua5">
        <span id="quote-files-label" className="text-[15px] font-semibold text-ink" data-id="element-249" data-mp="j0ucomlnd0eg">
          Photos or files
        </span>
        <span className="text-[13px] text-steel" data-id="element-250" data-mp="v81f435ri41d">Optional · up to {MAX_QUOTE_FILES}</span>
      </div>
      <p id="quote-files-hint" className="mt-1 text-sm text-steel" data-id="element-251" data-mp="1075nwsorxzr6">
        Damage photos, paint references or anything that helps show the work.
      </p>

      <div className="mt-3 flex flex-col gap-3 border border-dashed border-steel/40 bg-white p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5" data-id="element-252" data-mp="14aaouqt7htdp">
        <div className="min-w-0" data-id="element-253" data-mp="1e6nhdld0m16v">
          <input ref={inputRef} id="quote-files" type="file" multiple disabled={full} onChange={handleChange} aria-labelledby="quote-files-label quote-files-button-text" aria-describedby="quote-files-hint quote-files-status" className="peer sr-only" data-id="element-254" data-mp="1e5m3za8j12lz" />
          <label htmlFor="quote-files" className={`inline-flex min-h-[44px] items-center gap-2 border px-4 font-display text-[16px] font-semibold uppercase tracking-[0.06em] transition-colors duration-150 ease-out peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand ${full ? 'cursor-not-allowed border-line text-steel/60' : 'cursor-pointer border-ink/25 text-ink hover:border-ink'}`} data-id="element-255" data-mp="1wwvg7r9om5h9">
            <PaperclipIcon aria-hidden="true" className="h-4 w-4" data-id="element-256" data-mp="ghbhckut7tuz" />
            <span id="quote-files-button-text" data-id="element-257" data-mp="bj07490lrhc8">{files.length ? 'Add more files' : 'Choose files'}</span>
          </label>
        </div>
        <p id="quote-files-status" aria-live="polite" className="text-sm text-steel" data-id="element-258" data-mp="phqcebxksnbo">
          {full ? 'File limit reached' : `${remaining} of ${MAX_QUOTE_FILES} remaining`}
        </p>
      </div>

      {notice && <p role="status" className="mt-2 text-sm font-medium text-brand-deep" data-id="element-259" data-mp="2mc4dfqq2v9wc">
          {notice}
        </p>}

      {files.length > 0 && <ul aria-label="Selected files" className="mt-3 divide-y divide-line border border-line bg-white" data-id="element-260" data-mp="11vd8zkalbmub">
          {files.map((file, index) => <li key={`${file.name}-${index}`} className="flex min-w-0 items-center gap-3 py-2 pl-4 pr-2" data-id="element-261" data-mp="t1qgq45ld1pv" data-mpi={index}>
              <FileIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-steel" data-id="element-262" data-mp="x33oijs8vezf" />
              <span className="min-w-0 flex-1" data-id="element-263" data-mp="x0zedk3r5jqp">
                <span className="block truncate text-[15px] text-ink" title={file.name} data-id="element-264" data-mp="j2j8twhmlza5">
                  {file.name}
                </span>
                <span className="block text-[13px] text-steel" data-id="element-265" data-mp="o0awuff5h192">{formatFileSize(file.size)}</span>
              </span>
              <button type="button" onClick={() => handleRemove(index)} aria-label={`Remove ${file.name}`} className="inline-flex h-11 w-11 shrink-0 items-center justify-center text-steel transition-colors duration-150 ease-out hover:text-brand-deep" data-id="element-266" data-mp="e47vhbs4h6n6">
                <XIcon aria-hidden="true" className="h-4 w-4" data-id="element-267" data-mp="1jqte3v6rolko" />
              </button>
            </li>)}
        </ul>}
    </div>;
}